import { skipToken } from "@reduxjs/toolkit/query";
import { Color } from "@repo/app-types/constants/colors";
import {
  getOutcomeDefinitionFromPlaybook,
  type PlaybookCriterionNode,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  isTeammateATeamMember,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  COACHING_PERIOD_DURATION,
  computeTeammatePlaybookCoachingDates,
  computeUpcomingTeammatePlaybookCoachingDates,
  convertCoachingContentToStructuredContent,
  formatGoalEvaluationWindowLabel,
  prepareCoachingContentCriteria,
  type CoachingContentV1,
  type CoachingContentV2,
  type CoachingContentV3,
  type CriterionCoachingContent,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import { CoachingGoalSummary } from "@repo/ui/coachingNote/coachingGoalSummary";
import { TeammateCoachingNote } from "@repo/ui/coachingNote/coachingNote";
import { CoachingScoreTargetBar } from "@repo/ui/coachingNote/coachingScoreTargetBar";
import { Size } from "@repo/ui/helpers/helpers";
import { nestedControlsStyle } from "@repo/ui/nestedControls";
import { Pill } from "@repo/ui/Pills/pill";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { TextSkeleton } from "@repo/ui/Skeletons/skeletons";
import { renderStructuredContent } from "@repo/ui/structuredContent/structuredContentRenderer";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { IconAlertTriangle, IconSchool } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState, type FC } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { useLocation, useNavigate } from "react-router";
import {
  usePlaybookTeammateCoachingManagerInputsQuery,
  usePlaybookTeammateCoachingPeriodsQuery,
  usePlaybookTeammateCoachingQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { isFetchBaseQueryError } from "../../api/helpers/errors.js";
import { GradingContextInfoTrigger } from "../../components/assessment/gradingContextInfoTrigger.js";
import { useAppSelector } from "../../hooks/hook.js";
import type {
  AnalyticsProviderValue,
  CoachingAnalyticsScope,
  TeammatePageAnalyticsScope,
} from "../analytics/analyticsContext/scopes.js";
import { requireAnalyticsScope } from "../analytics/analyticsContext/useAnalyticsContexts.js";
import { buildPlaybookScope } from "../filters/buildFilterQueryArgs.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import {
  CoachingAnalyticsScopeProvider,
  CoachingGoalViewTracker,
} from "./coachingAnalyticsScope.js";
import { CoachingManagerActions } from "./coachingManagerInput/coachingManagerActions.js";
import { ManagerNoteSlotContainer } from "./coachingManagerInput/managerNoteSlotContainer.js";
import { NextWeekGoalCardContainer } from "./coachingManagerInput/nextWeekGoalCardContainer.js";
import { useUnsavedCoachingChangesGuard } from "./coachingManagerInput/useUnsavedCoachingChangesGuard.js";
import { CriterionTeamExamplesContainer } from "./criterionTeamExamplesContainer.js";
import { WeeklyCoachingGoalCardContainer } from "./weeklyCoachingGoalCardContainer.js";

export interface TeammateCoachingSectionProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
}

const COACHING_PERIOD_QUERY_PARAMETER = "coaching_period";

export const TeammateCoachingSection: FC<TeammateCoachingSectionProps> = (
  props,
) => {
  const { playbookId, teammateId } = props;
  const buildCoachingAnalyticsScope = useCallback(
    (
      scope: Omit<CoachingAnalyticsScope, keyof TeammatePageAnalyticsScope>,
    ): AnalyticsProviderValue<"coaching"> =>
      (parent) => {
        const teammatePageScope = requireAnalyticsScope(parent, "teammatePage");
        if (
          teammatePageScope.teammateId !== teammateId ||
          teammatePageScope.playbookId !== playbookId
        ) {
          throw new Error(
            "Coaching analytics do not match the parent teammate page scope",
          );
        }
        return { ...teammatePageScope, ...scope };
      },
    [playbookId, teammateId],
  );

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPeriodId = useMemo(
    () =>
      new URLSearchParams(location.search).get(COACHING_PERIOD_QUERY_PARAMETER),
    [location.search],
  );
  const selectPeriod = useCallback(
    (periodId: string) => {
      setSelectedPeriodId(periodId);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set(COACHING_PERIOD_QUERY_PARAMETER, periodId);
      navigate(
        { search: searchParams.toString(), hash: location.hash },
        { replace: true, state: location.state },
      );
    },
    [location.hash, location.search, location.state, navigate],
  );
  // Expansion is shared across all cards: expanding one expands them all so the
  // grid rows stay aligned.
  const [areCardsExpanded, setAreCardsExpanded] = useState(false);

  // Whether the manager-input editor is open (editors/reviewers only). Which
  // editor that is follows from the viewed period: the upcoming week edits the
  // goal, the current week edits the note.
  const [isEditingManagerInput, setIsEditingManagerInput] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { requestAction, guardModals } =
    useUnsavedCoachingChangesGuard(hasUnsavedChanges);

  const openManagerInputEditor = useCallback(() => {
    setIsEditingManagerInput(true);
    setHasUnsavedChanges(false);
  }, []);

  const closeManagerInputEditor = useCallback(() => {
    setIsEditingManagerInput(false);
    setHasUnsavedChanges(false);
  }, []);

  const currentTeammate = useAppSelector(selectTeammate);
  const isManager =
    !!currentTeammate && !isTeammateATeamMember(currentTeammate);

  const { data: periods } = usePlaybookTeammateCoachingPeriodsQuery({
    playbookId,
    teammateId,
  });

  const { data: managerInputsData } =
    usePlaybookTeammateCoachingManagerInputsQuery({ playbookId, teammateId });
  const upcomingPeriodId = managerInputsData?.upcoming_period_id;
  const latestPeriodId = managerInputsData?.latest_period_id ?? null;
  const managerInputByPeriodId = useMemo(
    () =>
      new Map(
        (managerInputsData?.manager_inputs ?? []).map((input) => [
          input.period_id,
          input,
        ]),
      ),
    [managerInputsData?.manager_inputs],
  );

  const { data: coachedTeammate } = useBasicTeammateQuery(teammateId);
  const teammateFirstName = coachedTeammate?.first_name || "This teammate";

  const requestedStoredPeriodId = periods?.find(
    ({ period_id }) => period_id === requestedPeriodId,
  )?.period_id;
  const effectivePeriodId =
    selectedPeriodId ?? requestedStoredPeriodId ?? periods?.[0]?.period_id;

  // "Next week": the upcoming, not-yet-generated period a manager-set goal
  // targets, reachable as an extra entry in the period selector. A stored
  // period with the same id (possible with custom cadences) wins.
  const isViewingUpcomingPeriod =
    isManager &&
    upcomingPeriodId !== undefined &&
    effectivePeriodId === upcomingPeriodId &&
    !(periods ?? []).some((p) => p.period_id === upcomingPeriodId);

  const {
    data: coachingData,
    currentData: currentCoachingData,
    error: coachingError,
    isFetching: isCoachingFetching,
    isLoading: isCoachingLoading,
    isError: isCoachingError,
  } = usePlaybookTeammateCoachingQuery(
    isViewingUpcomingPeriod
      ? skipToken
      : {
          playbookId,
          teammateId,
          periodId: effectivePeriodId,
        },
  );
  const isCoachingNotFound =
    isCoachingError &&
    isFetchBaseQueryError(coachingError) &&
    coachingError.status === 404;
  const hasCoachingQueryError = isCoachingError && !isCoachingNotFound;
  // On an operational refetch failure, keep only data belonging to the current
  // query args. That preserves an open editor and its draft without rendering a
  // cached result from a previously selected period or playbook.
  const coaching = isCoachingError ? currentCoachingData : coachingData;

  const {
    data: playbookData,
    currentData: currentPlaybookData,
    isFetching: isPlaybookFetching,
    isLoading: isPlaybookLoading,
    isError: isPlaybookError,
  } = usePlaybookByIdQuery(
    coaching
      ? coaching.recording_assessment_template_id
      : isViewingUpcomingPeriod
        ? playbookId
        : skipToken,
  );
  const playbook = isPlaybookError ? currentPlaybookData : playbookData;

  const now = useNow();
  const coachingDates = useMemo(
    () =>
      coaching ? computeTeammatePlaybookCoachingDates(coaching) : undefined,
    [coaching],
  );

  // The rep's current score on each criterion: the coaching score window's
  // moving average as of today, clamped to the goal evaluation window. Used to
  // plot progress toward each criterion's score target.
  const criteriaScoresArgs = useMemo(() => {
    if (!coachingDates) {
      return skipToken;
    }

    const { goalEvaluationWindowStart, goalEvaluationWindowEnd } =
      coachingDates;
    const asOf =
      now.toMillis() < goalEvaluationWindowStart.toMillis()
        ? goalEvaluationWindowStart
        : now.toMillis() > goalEvaluationWindowEnd.toMillis()
          ? goalEvaluationWindowEnd
          : now;

    return {
      playbookId,
      teammateId,
      scope: buildPlaybookScope(now, playbookId, {
        length: COACHING_PERIOD_DURATION,
        end: asOf,
      }),
      filters: [],
    };
  }, [coachingDates, now, playbookId, teammateId]);

  const { data: criteriaScoresData } =
    usePlaybookTeammatePlaybookCriteriaScoresQuery(criteriaScoresArgs);

  const currentScoreByCriterionId = useMemo(() => {
    if (!criteriaScoresData) {
      return undefined;
    }

    return new Map(
      criteriaScoresData.criteriaScores.map((score) => [
        score.criteria_node_id,
        score.average_score,
      ]),
    );
  }, [criteriaScoresData]);

  const criteria = useMemo(() => {
    if (!coaching?.content || !playbook?.assessment_tree) {
      return undefined;
    }

    return prepareCoachingContentCriteria(
      playbook.assessment_tree,
      coaching.content,
    ).flatMap(({ criterion, coaching }) =>
      criterion
        ? [
            {
              criterion,
              infoContent: criterion.grading_context || criterion.description,
              coaching,
            },
          ]
        : [],
    );
  }, [coaching?.content, playbook?.assessment_tree]);
  // The single-focus, score-target coaching that renders the modern trend-meter
  // goal card. It needs a concrete baseline below the target; malformed goals,
  // call-count goals, and a null snapshot keep the original note rendering.
  const focusCriterion = criteria?.length === 1 ? criteria[0] : undefined;
  const modernGoalCard = useMemo(() => {
    if (!focusCriterion) {
      return undefined;
    }

    const {
      content,
      goal,
      teammate_average_score: startedScore,
    } = focusCriterion.coaching;
    if (
      content.v !== 3 ||
      goal?.type !== "score_target" ||
      startedScore === null ||
      startedScore >= goal.score_target
    ) {
      return undefined;
    }

    return {
      criterion: focusCriterion.criterion,
      criterionContent: {
        ...focusCriterion.coaching,
        content,
        goal,
        teammate_average_score: startedScore,
      },
    };
  }, [focusCriterion]);

  // The manager goal/note entry points are only offered on the modern goal
  // card (and on the next-week view reached from it).
  const showsManagerControls =
    isManager &&
    upcomingPeriodId !== undefined &&
    (isViewingUpcomingPeriod || modernGoalCard !== undefined);

  // Human-readable range of the upcoming goal week ("next week"), derived the
  // same way as the stored periods: the goal evaluation window starts at the
  // period end and runs one period duration.
  const upcomingPeriodLabel = useMemo(() => {
    const upcomingPeriodEnd = managerInputsData?.upcoming_period_end;
    if (!upcomingPeriodEnd) {
      return undefined;
    }

    const periodEnd = DateTime.fromISO(upcomingPeriodEnd);
    if (!periodEnd.isValid) {
      return undefined;
    }

    return formatGoalEvaluationWindowLabel(
      computeUpcomingTeammatePlaybookCoachingDates(periodEnd),
    );
  }, [managerInputsData?.upcoming_period_end]);

  const periodOptions = useMemo(() => {
    const storedPeriodOptions = (periods ?? []).map((p) => {
      const coachingDates = computeTeammatePlaybookCoachingDates({
        content: p,
      });

      return {
        value: p.period_id,
        label: formatGoalEvaluationWindowLabel(coachingDates),
      };
    });

    // Managers can navigate to the upcoming week to set next week's goal.
    // Skip the extra entry when the latest stored period already carries it
    // (a custom-cadence period can overlap the computed upcoming id).
    if (
      showsManagerControls &&
      upcomingPeriodId &&
      upcomingPeriodLabel &&
      !storedPeriodOptions.some((option) => option.value === upcomingPeriodId)
    ) {
      return [
        { value: upcomingPeriodId, label: upcomingPeriodLabel },
        ...storedPeriodOptions,
      ];
    }

    return storedPeriodOptions;
  }, [periods, showsManagerControls, upcomingPeriodId, upcomingPeriodLabel]);

  const hasCoachingGoals = coaching?.content.criteria.some(
    (c) => c.goal != null,
  );
  const hasAchievedCoachingGoals = coaching?.content.criteria.every(
    (c) => !c.goal || checkIsCoachingGoalAchieved(c, currentScoreByCriterionId),
  );

  const coachingGoalsDaysRemaining = useMemo(() => {
    if (!coachingDates) {
      return undefined;
    }

    const deadline = coachingDates.goalEvaluationWindowEnd;

    return Math.max(0, Math.ceil(deadline.diff(now, "days").days));
  }, [now, coachingDates]);

  const periodSelectorNode =
    periodOptions.length > 0 ? (
      <SimpleSelect
        label="Coaching period"
        isLabelHidden
        options={periodOptions}
        value={effectivePeriodId ?? null}
        onChange={(value) =>
          requestAction(() => {
            closeManagerInputEditor();
            selectPeriod(value);
          })
        }
      />
    ) : null;

  const viewedManagerInput = effectivePeriodId
    ? managerInputByPeriodId.get(effectivePeriodId)
    : undefined;
  const isViewingLatestPeriod =
    !!effectivePeriodId && effectivePeriodId === latestPeriodId;
  const canEditNote = showsManagerControls && isViewingLatestPeriod;

  // Shown on the current week's goal card only. A past week's note can't be
  // edited once the next week's coaching is generated.
  const managerActionsNode = canEditNote ? (
    <CoachingManagerActions
      hasCurrentNote={!!viewedManagerInput?.note}
      hasNextGoal={!!managerInputByPeriodId.get(upcomingPeriodId)?.criterion_id}
      onAddNote={openManagerInputEditor}
      onSetNextGoal={() =>
        requestAction(() => {
          selectPeriod(upcomingPeriodId);
          openManagerInputEditor();
        })
      }
    />
  ) : null;

  // The manager note (or its editor) and the manager actions. Notes outside
  // the editable current week stay visible but read-only.
  const managerNoteSlotNode =
    !isViewingUpcomingPeriod &&
    effectivePeriodId &&
    (viewedManagerInput?.note || canEditNote) ? (
      <ManagerNoteSlotContainer
        playbookId={playbookId}
        teammateId={teammateId}
        periodId={effectivePeriodId}
        managerInput={viewedManagerInput}
        teammateFirstName={teammateFirstName}
        canEdit={canEditNote}
        isEditing={isEditingManagerInput}
        onOpenEditor={openManagerInputEditor}
        onCloseEditor={closeManagerInputEditor}
        onDirtyChange={setHasUnsavedChanges}
        actions={managerActionsNode}
      />
    ) : null;

  const hasOperationalQueryError = hasCoachingQueryError || isPlaybookError;
  const queryErrorNode = hasOperationalQueryError ? (
    <CoachingLoadError />
  ) : null;
  const canRenderCurrentContent = isViewingUpcomingPeriod
    ? playbook !== undefined
    : coaching !== undefined && criteria !== undefined;

  if (hasOperationalQueryError && !canRenderCurrentContent) {
    return (
      <>
        {periodSelectorNode ? (
          <div className="mb-3 flex justify-end">{periodSelectorNode}</div>
        ) : null}
        {queryErrorNode}
        {guardModals}
      </>
    );
  }

  if (isViewingUpcomingPeriod && upcomingPeriodId) {
    return (
      <CoachingAnalyticsScopeProvider
        value={buildCoachingAnalyticsScope({
          periodId: upcomingPeriodId,
          criterionId:
            managerInputByPeriodId.get(upcomingPeriodId)?.criterion_id ?? null,
        })}
      >
        <CoachingGoalViewTracker
          variant="next_week"
          isReady={playbook !== undefined}
        >
          {queryErrorNode}
          <NextWeekGoalCardContainer
            key="next-week-goal"
            playbookId={playbookId}
            playbook={playbook}
            teammateId={teammateId}
            upcomingPeriodId={upcomingPeriodId}
            managerGoal={managerInputByPeriodId.get(upcomingPeriodId)}
            teammateFirstName={teammateFirstName}
            headerControls={periodSelectorNode}
            isEditing={isEditingManagerInput}
            onOpenEditor={openManagerInputEditor}
            onCloseEditor={closeManagerInputEditor}
            onDirtyChange={setHasUnsavedChanges}
          />
        </CoachingGoalViewTracker>
        {guardModals}
      </CoachingAnalyticsScopeProvider>
    );
  }

  if (
    (!coaching || !criteria) &&
    (isCoachingLoading ||
      isCoachingFetching ||
      isPlaybookLoading ||
      isPlaybookFetching)
  ) {
    return (
      <div className="flex-auto p-3">
        <TextSkeleton />
      </div>
    );
  }

  if (!coaching || !criteria) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-border-light bg-white p-3 shadow-xs">
        <div className="flex max-w-sm flex-col items-center gap-2.5 text-sm">
          <IconSchool className="size-8 shrink-0 text-gray-300" />

          <div className="text-center text-gray-400">
            <div>
              Coaching suggestions will appear here once enough calls have been
              analyzed.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (coaching && playbook && focusCriterion?.coaching.content.v === 3) {
    const focusContent = focusCriterion.coaching.content;
    const focusScope = buildCoachingAnalyticsScope({
      periodId: coaching.period_id,
      criterionId: focusCriterion.criterion.id,
    });

    if (modernGoalCard) {
      return (
        <CoachingAnalyticsScopeProvider value={focusScope}>
          <CoachingGoalViewTracker variant="goal_card">
            {queryErrorNode}
            <WeeklyCoachingGoalCardContainer
              key="weekly-goal"
              playbookId={coaching.recording_assessment_template_id}
              teammateId={teammateId}
              coaching={coaching}
              criterion={modernGoalCard.criterion}
              criterionContent={modernGoalCard.criterionContent}
              outcomeDefinition={getOutcomeDefinitionFromPlaybook(playbook)}
              periodSelector={periodSelectorNode}
              managerNoteSlot={managerNoteSlotNode}
            />
          </CoachingGoalViewTracker>
          {guardModals}
        </CoachingAnalyticsScopeProvider>
      );
    }

    const focusCoaching = {
      ...focusCriterion.coaching,
      content: focusContent,
    };

    return (
      <CoachingAnalyticsScopeProvider value={focusScope}>
        <CoachingGoalViewTracker variant="note">
          {queryErrorNode}
          <TeammateCoachingNote
            playbookId={coaching.recording_assessment_template_id}
            teammateId={teammateId}
            criterion={focusCriterion.criterion}
            criterionContent={focusCoaching}
            currentScore={
              currentScoreByCriterionId?.get(focusCriterion.criterion.id) ??
              null
            }
            isCurrentScoreLoading={currentScoreByCriterionId === undefined}
            outcomeDefinition={getOutcomeDefinitionFromPlaybook(playbook)}
            periodSelector={periodSelectorNode}
            managerNoteSlot={managerNoteSlotNode}
          />
        </CoachingGoalViewTracker>
      </CoachingAnalyticsScopeProvider>
    );
  }

  const renderContent = () => {
    const playbookId = coaching.recording_assessment_template_id;

    return (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {criteria.map(({ criterion, infoContent, coaching }) => (
          <Disclosure
            key={criterion.id}
            isExpanded={areCardsExpanded}
            onExpandedChange={setAreCardsExpanded}
            className="rounded-lg border border-border-light bg-white"
          >
            <Heading
              className={nestedControlsStyle.container({
                className: "group w-full min-w-0",
              })}
            >
              <div className="space-y-4 p-4">
                {/* Criterion name */}
                <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-muted-foreground">
                  <span className="truncate">{criterion.name}</span>

                  {infoContent ? (
                    <div
                      className={nestedControlsStyle.secondaryControl({
                        className: "flex",
                      })}
                    >
                      <GradingContextInfoTrigger
                        title={criterion.name}
                        content={infoContent}
                      />
                    </div>
                  ) : null}
                </div>

                {/* Focus this week + progress */}
                {coaching.goal?.type === "score_target" ? (
                  <>
                    <FocusThisWeekBox sentence={coaching.goal.summary} />
                    <CoachingScoreTargetBar
                      currentScore={
                        currentScoreByCriterionId?.get(criterion.id) ?? null
                      }
                      target={coaching.goal.score_target}
                      gradingValueType={criterion.grading_value_type}
                      isLoading={currentScoreByCriterionId === undefined}
                    />
                  </>
                ) : coaching.goal ? (
                  <CoachingGoalSummary
                    goal={coaching.goal}
                    goalProgress={coaching.goal_progress}
                  />
                ) : (
                  <FocusThisWeekBox sentence={coaching.summary} />
                )}

                {/* Expand / collapse affordance. The whole header is clickable
                    via the primary control's stretched hit area. */}
                <AriaButton
                  slot="trigger"
                  className={nestedControlsStyle.primaryControl({
                    className:
                      "text-left text-xs font-medium text-muted-foreground hover:text-foreground",
                  })}
                >
                  {areCardsExpanded ? "See less" : "See more"}
                </AriaButton>
              </div>
            </Heading>

            {/* Expanded coaching description */}
            <DisclosurePanel>
              <CoachingAnalyticsScopeProvider
                value={(parent) => ({
                  ...requireAnalyticsScope(parent, "coaching"),
                  criterionId: criterion.id,
                })}
              >
                <div className="mx-4 mb-4">
                  {renderCoachingContent(
                    teammateId,
                    playbookId,
                    criterion,
                    coaching.content,
                  )}
                </div>
              </CoachingAnalyticsScopeProvider>
            </DisclosurePanel>
          </Disclosure>
        ))}
      </div>
    );
  };

  return (
    <CoachingAnalyticsScopeProvider
      value={buildCoachingAnalyticsScope({
        periodId: coaching.period_id,
        criterionId: null,
      })}
    >
      <CoachingGoalViewTracker
        variant="legacy_grid"
        isReady={criteria.length > 0}
      >
        {queryErrorNode}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Coaching goals
            </Heading>

            {periodOptions.length > 0 ? (
              <div className="flex items-center gap-2">
                {periodSelectorNode}

                {hasCoachingGoals && hasAchievedCoachingGoals ? (
                  <Pill
                    label="Challenge completed"
                    size={Size.SMALL}
                    color={Color.Green}
                  />
                ) : null}

                {hasCoachingGoals &&
                !hasAchievedCoachingGoals &&
                coachingGoalsDaysRemaining !== undefined &&
                coachingGoalsDaysRemaining <= 0 ? (
                  <Pill
                    label="Challenge ended"
                    size={Size.SMALL}
                    color={Color.Gray}
                  />
                ) : null}

                {hasCoachingGoals &&
                !hasAchievedCoachingGoals &&
                coachingGoalsDaysRemaining !== undefined &&
                coachingGoalsDaysRemaining > 0 ? (
                  <Pill
                    label={renderPlural(coachingGoalsDaysRemaining, {
                      one: "1 day remaining",
                      other: (n) => `${n.toLocaleString()} days remaining`,
                    })}
                    size={Size.SMALL}
                    color={Color.Blue}
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          {managerNoteSlotNode}

          <div className="flex flex-col text-sm text-foreground">
            {renderContent()}
          </div>
        </div>
      </CoachingGoalViewTracker>
    </CoachingAnalyticsScopeProvider>
  );
};

const CoachingLoadError: FC = () => (
  <div
    role="alert"
    className="mb-3 flex items-center justify-center rounded-lg border border-border-light bg-white p-3 shadow-xs"
  >
    <div className="flex max-w-sm flex-col items-center gap-2.5 text-sm">
      <IconAlertTriangle className="size-8 shrink-0 text-destructive" />
      <div className="text-center text-muted-foreground">
        Unable to load coaching suggestions. Refresh the page to try again.
      </div>
    </div>
  </div>
);

const FocusThisWeekBox: FC<{ sentence: string }> = ({ sentence }) => (
  <div className="rounded-md bg-gray-50 p-3 ring-1 ring-gray-200/60">
    <p className="text-xs font-medium text-muted-foreground">Focus this week</p>
    <p className="mt-1 text-sm font-semibold text-foreground">{sentence}</p>
  </div>
);

/**
 * Whether the rep has met a criterion's coaching goal: reaching the target
 * score for score-target goals, or performing the behavior on the target number
 * of calls for call-count goals.
 */
function checkIsCoachingGoalAchieved(
  criterion: CriterionCoachingContent,
  currentScoreByCriterionId: Map<string, number> | undefined,
): boolean {
  const goal = criterion.goal;
  if (!goal) {
    return false;
  }

  if (goal.type === "score_target") {
    const currentScore = currentScoreByCriterionId?.get(criterion.criterion_id);
    return currentScore !== undefined && currentScore >= goal.score_target;
  }

  const observedCount = criterion.goal_progress?.observed_count ?? 0;
  return observedCount >= goal.goal_call_count;
}

function renderCoachingContent(
  teammateId: TeammateId,
  playbookId: PlaybookId,
  criterion: PlaybookCriterionNode,
  content: CoachingContentV1 | CoachingContentV2 | CoachingContentV3,
) {
  const structuredContent = renderStructuredContent(
    convertCoachingContentToStructuredContent(content),
    { teammateId, playbookId, criterionId: criterion.id },
  );

  // Legacy V1 notes predate the sectioned format: keep their "Overview" label
  // and the separately fetched team examples.
  if (content.v === undefined) {
    return (
      <>
        <div className="text-sm text-foreground">
          <div className="mb-2 text-xs font-semibold text-muted-foreground">
            Overview
          </div>
          {structuredContent}
        </div>

        <CriterionTeamExamplesContainer
          playbookId={playbookId}
          teammateId={teammateId}
          criterion={criterion}
        />
      </>
    );
  }

  return <div className="text-sm text-foreground">{structuredContent}</div>;
}
